import axios from "axios";

/**
 * A shared brake for one upload session.
 *
 * Retries used to be per-file and independent. On 2026-08-30 19:34 UTC the
 * storage path refused every request for four minutes; each file burned its six
 * attempts in about forty seconds (a 502 fails instantly, so the 1/2/4/8/10/10s
 * backoff is all the waiting there is) and was declared permanently lost —
 * while the backend recovered at 19:39. Customers saw "47 of 48 photos failed"
 * for an outage nothing needed to be lost to.
 *
 * Independent retries also meant all ~1,400 files hammered a backend that was
 * already refusing, which is the opposite of helpful.
 *
 * So the failure is shared. A short run of backend-wide failures stalls every
 * upload at once; whoever arrives next waits on a long backoff; the first
 * success releases everyone. A file's own deadline must exclude `stalledMs()`,
 * otherwise the wait we just did would expire the budget and lose the photo
 * anyway.
 */

/** Consecutive backend-wide failures before we stop and wait. */
const FAILURES_BEFORE_STALL = 4;

/**
 * Backoff while stalled. Deliberately long: the measured outage was ~4 minutes
 * and self-healing, so waiting is strictly better than discarding a customer's
 * photos. Sums to well over four minutes before the cap.
 */
const STALL_BACKOFF_MS = [5_000, 15_000, 30_000, 60_000, 60_000, 60_000, 90_000];

/**
 * Total time we are willing to spend stalled before giving up on the session.
 * Ten minutes is 2.5x the measured outage. Past this something is properly
 * broken and failing loudly beats a spinner that never ends.
 */
export const MAX_STALL_MS = 10 * 60_000;

/**
 * Is this failure about the backend, or about this one file?
 *
 * Only the former should pause everyone. A 403 (expired signature) or 404 (bad
 * key) belongs to a single upload; stalling the other 1,399 because of it would
 * be a new bug. ECONNABORTED is our own per-attempt timeout — a slow client
 * upload, already handled by the size-scaled timeout policy.
 */
export function isBackendWideFailure(error: unknown): boolean {
  if (!axios.isAxiosError(error)) return false;
  if (error.code === "ECONNABORTED") return false;
  const status = error.response?.status ?? 0;
  // No response at all: what a 502 without CORS headers looks like from the
  // browser, and what the incident actually produced.
  if (status === 0) return true;
  return status === 502 || status === 503 || status === 504;
}

export interface UploadGateDeps {
  now?: () => number;
  sleep?: (ms: number) => Promise<void>;
}

export interface UploadGate {
  /** Await before each attempt. Returns immediately unless stalled. */
  beforeAttempt: () => Promise<void>;
  recordSuccess: () => void;
  recordFailure: (error: unknown) => void;
  isStalled: () => boolean;
  isExhausted: () => boolean;
  /** Total ms spent waiting on the gate, to exclude from per-file deadlines. */
  stalledMs: () => number;
}

export function createUploadGate(deps: UploadGateDeps = {}): UploadGate {
  const now = deps.now ?? (() => Date.now());
  const sleep =
    deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));

  let consecutiveBackendFailures = 0;
  let stallStep = 0;
  let exhausted = false;
  // WALL-CLOCK start of the current stall, not a sum of sleeps. Three files
  // wait concurrently, so summing each waiter's sleep counted a 90s stall as
  // 270s and burned the budget ~3x too fast -- which is how a brief hiccup
  // exhausted a ten-minute budget in about three minutes and aborted 289 of
  // one customer's 340 photos on 2026-09-07.
  let stallStartedAt: number | null = null;
  let settledStalledMs = 0;

  const currentStallMs = () =>
    stallStartedAt === null ? 0 : Math.max(0, now() - stallStartedAt);

  const stalled = () =>
    !exhausted && consecutiveBackendFailures >= FAILURES_BEFORE_STALL;

  return {
    async beforeAttempt() {
      if (!stalled()) return;
      if (stallStartedAt === null) stallStartedAt = now();
      const wait = STALL_BACKOFF_MS[Math.min(stallStep, STALL_BACKOFF_MS.length - 1)];
      stallStep += 1;
      await sleep(wait);
      if (settledStalledMs + currentStallMs() >= MAX_STALL_MS) exhausted = true;
    },

    recordSuccess() {
      // The backend answered. Release everyone, forget the run of failures --
      // and CLEAR exhaustion. This used to be a one-way latch, so once the
      // budget ran out every remaining file threw "storage unavailable for too
      // long" without even attempting an upload, and no recovery was possible.
      // A gate may add patience; it must never remove attempts.
      consecutiveBackendFailures = 0;
      stallStep = 0;
      exhausted = false;
      settledStalledMs += currentStallMs();
      stallStartedAt = null;
    },

    recordFailure(error: unknown) {
      if (isBackendWideFailure(error)) {
        consecutiveBackendFailures += 1;
      } else {
        // A per-file failure says nothing about the backend, so it must not
        // push us toward a stall — but it must not clear a real one either.
        consecutiveBackendFailures = Math.max(0, consecutiveBackendFailures);
      }
    },

    isStalled: stalled,
    isExhausted: () => exhausted,
    stalledMs: () => settledStalledMs + currentStallMs(),
  };
}
